import React from 'react';
import { Box, Text, HStack, VStack, Icon, SimpleGrid, Button } from '@chakra-ui/react';
import { FiCheckCircle, FiClock, FiAlertTriangle, FiKey, FiFacebook, FiRefreshCw } from 'react-icons/fi';
import Card from 'components/card/Card';
import { StatusPill, ago, until } from './_shared';

/**
 * The Meta account behind the numbers.
 *
 * A tenant's numbers all hang off one WhatsApp Business Account, and when
 * something stops working it is usually the account, not the number: the
 * subscription drifted, the token was revoked, Meta restricted it. That state
 * lives only in the connection service, so it is shown here rather than left to
 * be guessed from a number that quietly stopped receiving.
 */

// ★ `reconnect` IS ON THE STATE, NOT ON "IS SOMETHING WRONG".
// Reconnecting re-reads the workspace token and re-seals the credential, which
// fixes a revoked token and an accounts-level disconnect — and fixes NOTHING
// about a Meta restriction or a policy violation. Offering the button on those
// would be a control that cannot work, which is worse than no control: it sends
// an administrator round a loop that always ends where it started.
const STATE = {
	ACTIVE: { tone: 'success', icon: FiCheckCircle, text: 'Connected and receiving' },
	SUBSCRIPTION_PENDING: { tone: 'warning', icon: FiClock, text: 'Waiting for Meta to confirm the subscription' },
	NEEDS_REAUTH: {
		tone: 'danger',
		icon: FiAlertTriangle,
		text: 'Reconnect needed — the access token is no longer valid',
		reconnect: true,
	},
	RESTRICTED: { tone: 'danger', icon: FiAlertTriangle, text: 'Meta has restricted this account' },
	DISCONNECTED: {
		tone: 'danger',
		icon: FiAlertTriangle,
		text: 'Disconnected at Meta',
		reconnect: true,
	},
};

const Field = ({ label, children }) => (
	<Box>
		<Text fontSize="0.65rem" color="text.muted" textTransform="uppercase" letterSpacing="wide">
			{label}
		</Text>
		<Text fontSize="sm" color="text.primary" mt="0.5" wordBreak="break-all">
			{children}
		</Text>
	</Box>
);

export default function MetaAccountCard({ account, onReconnect }) {
	const state = STATE[account.status] ?? {
		tone: 'muted',
		icon: FiClock,
		text: account.status ?? 'Unknown',
	};
	const viaOwnApp = account.source === 'manual';

	// ★ THE TOKEN'S LIFETIME IS SAID BEFORE IT MATTERS. A token pasted from the
	// App Dashboard lives hours; the account reads ACTIVE right up to the first
	// send that fails with 190. The connection layer now records what
	// /debug_token said at connect time, so the warning appears while there is
	// still time to replace it — and an expired one offers Reconnect even though
	// the account status has not caught up yet.
	const token = account.token?.inspected ? account.token : null;
	const expiresAt = token && !token.neverExpires && token.expiresAt ? new Date(token.expiresAt) : null;
	const tokenExpired = Boolean(expiresAt && expiresAt.getTime() <= Date.now());
	const tokenLine = !token
		? null
		: token.neverExpires
			? { tone: 'muted', icon: FiCheckCircle, text: 'Permanent access token — never expires.' }
			: tokenExpired
				? {
						tone: 'danger',
						icon: FiAlertTriangle,
						text: `Access token expired ${ago(expiresAt)} — sending stops until a new token is saved in Admin Settings → Secrets and the account is reconnected.`,
					}
				: {
						tone: 'warning',
						icon: FiClock,
						text: `Temporary access token — expires ${until(expiresAt)}. Generate a System User token (Meta Business Settings → System Users, expiry “Never”), save it in Admin Settings → Secrets, then Reconnect.`,
					};

	return (
		<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
			<Box p="4">
				<HStack justify="space-between" align="start" mb="3" spacing="3">
					<HStack spacing="2.5" align="start" minW="0">
						<Icon as={state.icon} color={state.tone === 'muted' ? 'text.muted' : state.tone} boxSize="5" mt="0.5" />
						<Box minW="0">
							<Text fontSize="sm" fontWeight="bold" color="text.primary">
								WhatsApp Business Account
							</Text>
							<Text fontSize="xs" color="text.secondary" mt="0.5">
								{state.text}
							</Text>
						</Box>
					</HStack>
					<HStack spacing="2" flexShrink={0}>
						{/* ★ THE SCREEN USED TO SAY "RECONNECT NEEDED" AND OFFER NOWHERE
						    TO DO IT. The only route was to know that re-running the connect
						    form is what re-seals the token — which is not discoverable, and
						    was asked about directly. The button does not connect anything
						    itself; it opens that form with these ids already in it, so there
						    is still exactly one place a connection is made. */}
						{(state.reconnect || tokenExpired) && onReconnect && (
							<Button
								size="xs"
								variant="outline"
								colorScheme="red"
								leftIcon={<Icon as={FiRefreshCw} />}
								onClick={onReconnect}
							>
								Reconnect
							</Button>
						)}
						<StatusPill tone={state.tone}>{account.status}</StatusPill>
					</HStack>
				</HStack>

				<SimpleGrid columns={{ base: 1, md: 3 }} spacing="3">
					<Field label="Account ID">{account.wabaId}</Field>
					<Field label="Connected via">
						<HStack spacing="1.5">
							<Icon as={viaOwnApp ? FiKey : FiFacebook} color="text.muted" boxSize="3.5" />
							<Box as="span">{viaOwnApp ? `Your own Meta app${account.appId ? ` (${account.appId})` : ''}` : 'Facebook sign-in'}</Box>
						</HStack>
					</Field>
					<Field label="Connected">{ago(account.connectedAt) ?? 'Unknown'}</Field>
				</SimpleGrid>

				{account.subscriptionVerifiedAt && (
					<Text fontSize="xs" color="text.muted" mt="3">
						Meta confirmed the message subscription {ago(account.subscriptionVerifiedAt)}.
					</Text>
				)}

				{tokenLine && (
					<HStack spacing="1.5" align="start" mt="2">
						<Icon
							as={tokenLine.icon}
							color={tokenLine.tone === 'muted' ? 'text.muted' : tokenLine.tone}
							boxSize="3.5"
							mt="0.5"
						/>
						<Text fontSize="xs" color={tokenLine.tone === 'muted' ? 'text.muted' : 'text.secondary'}>
							{tokenLine.text}
						</Text>
					</HStack>
				)}
			</Box>
		</Card>
	);
}
